import { getRequest, postRequest } from "api/api";
import { setElectricityState } from "app/features/electricity";
import {
  setBillers,
  setInitiatedTransaction,
  setInitiatedTranValues,
  setLoadBuyButton,
  setLoadToken,
  setShowTransReceipt,
  setSuccessTransactionDetails,
  setTotalAmount,
  setTransaction,
  setTransactions,
  setTransHistory,
  setTransLoading,
  setValidatedCustomer,
  setValidatedCustomerDetails,
  setValidatingCustomer,
} from "app/features/transactions";
import { useAppDispatch, useAppSelector } from "app/hooks";
import axios, { AxiosError } from "axios";
import { createContext, useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { SelectOptionType } from "types/shared";
import {
  InitiateTransValueType,
  TransactionType,
  ValidateCustomerType,
} from "types/transaction";
import { closePaymentModal } from "flutterwave-react-v3";
import { trackMixpanelEvent } from "analytics";
import { formatTo234 } from "utils/formatter";

interface ErrorFieldType {
  meterType: string;
  distributor: string;
  totalAmount: string;
}

interface TransactionContextType {
  fetchTransactions: () => Promise<void>;
  fetchTransHistory: () => Promise<void>;
  fetchTransaction: (value: string) => Promise<void>;
  validateCustomer: (value: ValidateCustomerType) => Promise<void>;
  fetchBillers: () => Promise<void>;
  handleInitiateTransaction: (value: InitiateTransValueType) => Promise<void>;
  // getTransactionStatus: (val: any) => Promise<void>;
  handleBillPayment: () => Promise<void>;
  handleGetToken: (val: string) => Promise<void>;
  handleCheckTransactionStatus: (id: string) => Promise<void>;
  handleVerifyTransaction: (val: any) => Promise<void>;
  validateFields: (val: InitiateTransValueType) => boolean;
  error: ErrorFieldType;
}

const TransactionContext = createContext({} as TransactionContextType);
interface Props {
  children: React.ReactNode;
}
const TransactionProvider = ({ children }: Props) => {
  const dispatch = useAppDispatch();
  const {
    customerDetails,
    validatedCustomer,
    initiatedTransValues,
    requestedDetails,
  } = useAppSelector((state) => state.transactions);
  const [error, setError] = useState({} as ErrorFieldType);

  const validateFields = (value: InitiateTransValueType) => {
    const errors: any = {};
    if (value.distributor === "" || value.distributor === undefined) {
      errors.distributor = "Please select a distributor";
    }
    if (value.meterType === "" || value.distributor === undefined) {
      errors.meterType = "Please select a meter type";
    }
    if (!value.totalAmount) {
      errors.totalAmount = "Please enter a valid amount";
    }
    setError(errors);
    return Object.keys(errors).length === 0;
  };

  const fetchTransactions = useCallback(async () => {
    try {
      const { data } = await postRequest("transactions/list", { page: 1 });
      console.log(data);
      dispatch(setTransactions(data.data.list));
    } catch (_err) {
      // const error = err as AxiosError;
      // console.error("all transactions", error);
    }
    // eslint-disable-next-line
  }, []);

  const fetchTransHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(
        process.env.REACT_APP_AUTH_BASE_URL + "/transaction/history/analysis",
      );
      dispatch(setTransHistory(data));
    } catch (err) {
      const error = err as AxiosError;
      console.error("transaction history", error.status);
    }
    // eslint-disable-next-line
  }, []);

  const fetchTransaction = async (id: string) => {
    try {
      dispatch(setTransLoading(true));
      const { data } = await postRequest(`transactions/read`, { id });
      dispatch(setTransaction(data.data));
    } catch (err) {
      const error = err as AxiosError;
      console.error("single transaction", error);
    } finally {
      dispatch(setTransLoading(false));
    }
  };

  const fetchBillers = useCallback(async () => {
    try {
      const { data } = await getRequest("billers");
      const newBillers: SelectOptionType[] = [];
      data?.data.map((item: any) => {
        return newBillers.push({ value: item.billerCode, label: item.name });
      });
      dispatch(setBillers(newBillers));
    } catch (_err) {
      // console.log(error);
    }
    // eslint-disable-next-line
  }, []);

  const validateCustomer = async (value: ValidateCustomerType) => {
    dispatch(setValidatingCustomer(true));
    try {
      const { data } = await postRequest("meters/look-up", {
        meterType: initiatedTransValues.meterType?.toLowerCase(),
        meterNumber: initiatedTransValues.meterNumber,
        // billerCode: "BIL119",
        billerCode: value.billerCode,
        amount: initiatedTransValues.totalAmount,
        email: initiatedTransValues.email,
        currency: "NGN",
      });
      if (data.status) {
        dispatch(setValidatedCustomer(true));
        dispatch(setValidatedCustomerDetails(data.data));
      }
    } catch (_err) {
      // console.log("validata customer", error);
      toast.error(
        "Unable to verify the meter number at this time. Please, try again in a few minutes",
      );
    } finally {
      dispatch(setValidatingCustomer(false));
    }
  };

  const handleInitiateTransaction = async (value: InitiateTransValueType) => {
    if (validatedCustomer) {
      dispatch(setLoadBuyButton(true));
      dispatch(setLoadToken(true));
      try {
        const { data, status } = await postRequest(
          "transaction/initiate",
          value,
        );
        dispatch(setInitiatedTransaction(data));
        dispatch(setTotalAmount(value.totalAmount));
        if (status === 201) {
          const flutterwaveConfig = {
            public_key: process.env.REACT_APP_FLW_PUBLIC_ID,
            amount: value.totalAmount,
            tx_ref: data.referenceNumber,
            currency: "NGN",
            payment_options: "card,ussd,banktransfer,nqr,barter",
            customer: {
              email: value.email,
              phone_number: value.phoneNumber,
              name: customerDetails.customerName,
            },
            customizations: {
              title: "PAWA360",
              description: "Payment for Light",
              logo: "assets/icons/logo.svg",
            },
            callback: (val: any) => {
              if (val.status === "successful" || val.status === "completed") {
                trackMixpanelEvent("Successful Payment", {
                  amount: value.totalAmount,
                  email: value.email,
                  phone: value.phoneNumber,
                  meterNumber: value.meterNumber,
                  meterType: value.meterType,
                  distributor: value.distributor!,
                });
                handleVerifyTransaction({ data: val, id: data.id });
                // closePaymentModal();
              }
              setTimeout(() => {
                try {
                  closePaymentModal();
                } catch (error) {
                  toast(
                    "Please, close the pop-up window if it does not close automatically.",
                  );
                }
              }, 3000);
            },
            onclose: () => {
              dispatch(
                setInitiatedTranValues({
                  meterType: "",
                  distributor: "",
                  totalAmount: 0,
                  meterNumber: "",
                  email: "",
                  phoneNumber: "",
                } as InitiateTransValueType),
              );
              dispatch(setTransaction({} as TransactionType));
              dispatch(setValidatedCustomer(false));
              dispatch(setElectricityState("buy"));

              if (requestedDetails) {
                window.location.reload();
              }
            },
          };

          if (typeof (window as any).FlutterwaveCheckout === "undefined") {
            console.error("Flutterwave script not loaded");
            return;
          }
          (window as any).FlutterwaveCheckout(flutterwaveConfig);
        }
      } catch (err) {
        const error = err as AxiosError;
        toast.error(error.message);
        dispatch(setLoadToken(false));
      } finally {
        dispatch(setLoadBuyButton(false));
      }
    } else {
      toast.error("Please, enter a valid meter number");
    }
  };

  const handleVerifyTransaction = async (val: any) => {
    dispatch(setValidatedCustomer(false));
    dispatch(setElectricityState("buy"));
    dispatch(setShowTransReceipt(true));
    dispatch(setLoadToken(true));

    const value = {
      transactionReference: val.data.tx_ref,
      flutterwaveTransactionId: parseInt(val.data.transaction_id),
      status: val.data.status === "completed" ? "successful" : val.data.status,
      amount: val.data.amount,
      currency: val.data.currency,
    };
    try {
      const { status } = await postRequest("transaction/verify", value);
      // console.log(data);
      if (status === 200) {
        // setLoadToken(true);
        if (initiatedTransValues.meterType === "PREPAID") {
          toast(
            "Please wait. Your token will be displayed shortly. It will also be sent to the email address you provided.",
            { duration: 8000 },
          );
        }
        handleBillPayment();
      }
    } catch (err) {
      // console.log("validate trasaction", err);
      dispatch(setShowTransReceipt(false));
      dispatch(setLoadToken(false));
      toast.error("We are unable to verify this transaction");
    }
  };
  // const getTransactionStatus = async (val: any) => {
  //   try {
  //     const { data } = await getRequest(`transaction/${val.id}/status`);
  //     console.log(data === "Success");
  //     if (data === "Success") {
  //       handleBillPayment({ ref: val });
  //     }
  //     handleBillPayment({ ref: val });
  //   } catch (err) {
  //     console.log("trasaction status", err);
  //   }
  // };
  // const openCheckout = async (data: BillPaymentType) => {
  //   if (!(window as any).Squad) {
  //     toast.error("Payment service unavailable");
  //     return;
  //   }
  //   const squadInstance = new (window as any).Squad({
  //     key: process.env.REACT_APP_SQUAD_PUBLIC_KEY,
  //     reference: data.referenceNumber,
  //     email: data.email.trim(),
  //     amount: Number(data.totalAmount) * 100,
  //     currency_code: "NGN",
  //     onClose: () => {
  //       dispatch(setShowTransReceipt(false));
  //       dispatch(setLoadToken(false));
  //       dispatch(
  //         setInitiatedTranValues({
  //           meterType: "",
  //           distributor: "",
  //           totalAmount: 0,
  //           meterNumber: "",
  //           email: "",
  //           phoneNumber: "",
  //         } as InitiateTransValueType),
  //       );
  //       dispatch(setTransaction({} as TransactionType));
  //       dispatch(setValidatedCustomer(false));
  //     },
  //     onLoad: () => console.log("Widget loaded successfully"),
  //     // callback_url: `${window.location.origin}?reference=${data.referenceNumber}`,
  //     onSuccess: () => {
  //       console.log("Widget loaded successfully");
  //       localStorage.setItem("reference", data.referenceNumber);
  //     },
  //   });
  //   squadInstance.setup();
  //   squadInstance.open();
  // };
  const handleBillPayment = async () => {
    dispatch(setLoadToken(true));
    try {
      const { data } = await postRequest("bill-payments/checkout", {
        // amount: calculateElecCost(parseInt(val.data.amount)),
        amount: Number(initiatedTransValues.totalAmount),
        currency: "NGN",
        email: initiatedTransValues.email,
        meterId: customerDetails.id,
        phoneNumber: formatTo234(initiatedTransValues.phoneNumber),
        meterType: initiatedTransValues.meterType?.toLowerCase(),
        meterNumber: initiatedTransValues.meterNumber,
        billerCode: customerDetails.billerCode,
        customerName: customerDetails.customerName,
        payerName: customerDetails.customerName,
      });
      if (data.status) {
        dispatch(setSuccessTransactionDetails(data.data));
        dispatch(setLoadToken(false));
        localStorage.setItem("reference", data.data.referenceNumber);
        // window.open(data.data.checkOutURL);
        window.location.replace(data.data.checkOutURL);
        // openCheckout(data.data);
        fetchTransHistory();
        fetchTransactions();
      }
    } catch (err) {
      dispatch(setShowTransReceipt(false));
      toast.error("Something went wrong, Please contact Support");
      dispatch(setShowTransReceipt(false));
      dispatch(setLoadToken(false));
      dispatch(
        setInitiatedTranValues({
          meterType: "",
          distributor: "",
          totalAmount: 0,
          meterNumber: "",
          email: "",
          phoneNumber: "",
        } as InitiateTransValueType),
      );
      dispatch(setTransaction({} as TransactionType));
      localStorage.removeItem("reference");
    } finally {
      dispatch(setValidatedCustomer(false));
    }
  };
  const handleCheckTransactionStatus = async (id: string) => {
    dispatch(setLoadToken(true));
    try {
      const { data } = await postRequest(`transactions/read-status-by-ref`, {
        ref: id,
      });

      if (data.data.transactionStatus === "Success") {
        await handleGetToken(id);
      }
      if (data.data.transactionStatus === "Abandoned" || data.data.transactionStatus === "Uncompleted") {
        toast.error("Transaction was not completed. Please try again.");
        dispatch(setShowTransReceipt(false));
        dispatch(setLoadToken(false));
        localStorage.removeItem("reference");
      }
    } catch (err) {
      toast.error("Something went wrong, Please contact Support");
      dispatch(setShowTransReceipt(false));
      dispatch(setLoadToken(false));
      localStorage.removeItem("reference");
    }
  };
  const handleGetToken = async (id: string) => {
    dispatch(setLoadToken(true));
    try {
      const { data } = await postRequest(`transactions/read-by-ref`, {
        ref: id,
      });

      if (data.status) {
        dispatch(setSuccessTransactionDetails(data.data));
        dispatch(setLoadToken(false));
        dispatch(setShowTransReceipt(true));
      }
    } catch (err) {
      dispatch(setShowTransReceipt(false));
      toast.error("Something went wrong, Please contact Support");
      dispatch(setShowTransReceipt(false));
      dispatch(setLoadToken(false));
      dispatch(
        setInitiatedTranValues({
          meterType: "",
          distributor: "",
          totalAmount: 0,
          meterNumber: "",
          email: "",
          phoneNumber: "",
        } as InitiateTransValueType),
      );
      dispatch(setTransaction({} as TransactionType));
    } finally {
      dispatch(setValidatedCustomer(false));
      localStorage.removeItem("reference");
    }
  };
  useEffect(() => {
    fetchBillers();
  }, [fetchBillers]);
  return (
    <TransactionContext.Provider
      value={{
        fetchTransactions,
        fetchTransHistory,
        fetchTransaction,
        validateCustomer,
        fetchBillers,
        handleInitiateTransaction,
        handleBillPayment,
        handleVerifyTransaction,
        error,
        validateFields,
        handleGetToken,
        handleCheckTransactionStatus,
      }}
    >
      {children}
    </TransactionContext.Provider>
  );
};

export { TransactionProvider, TransactionContext };
